import { useCallback, useEffect, useState } from 'react'
import { usePagination } from 'hooks/pagination.hook'
import { TeenagerRecordAuditType } from '../../../../types/teenager-record-audit.type'
import client from '../../../../services/client'
import { BaseData, BaseDataContent } from '../../../../types/base.type'
import { DownloadResumePdf } from '../../../../types/download.type'
import { downloadFile } from 'utils/download.util'
import { useBackdrop } from '../../../../contexts/backdrop.context'
import { HttpErrorType } from '../../../../types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'
import { useSnackBar } from '../../../../contexts/snackbar.context'

export type TeenagerRecordAuditItem = {
  id: number
  pessoaFisica: {
    id: number
    nome: string
    nomeSocial?: string
    dataNascimento: string
    nomeMae?: string
    nomePai?: string
    genero?: {
      id: number
      nome: string
    }
  }
}

export type AuditItem = {
  dataEvento?: string
  funcionalidade?: string
  tipoEvento?: string
  usuario?: string
  campo?: string
  valorAntigo?: string
  valorNovo?: string
}

export const useTeenagerRecordAudit = () => {
  const {
    page,
    rows: { rowsPerPage },
    pages: { handleTotalOfPages, totalOfPages },
    elements: { totalOfElements, handleTotalOfElements },
    handleNewPage,
  } = usePagination()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const [teenagerRecordAuditItem, setTeenagerRecordAuditItem] = useState<TeenagerRecordAuditItem[]>(
    [],
  )

  const handleBuscarAdolescentes = async (data?: TeenagerRecordAuditType, newPage?: number) => {
    if (data) {
      handleBackdropOpen()
      await client
        .get<BaseDataContent<TeenagerRecordAuditItem[]>>('/ficha-adolescente/auditoria', {
          ...data,
          acao: data?.acao && data?.acao !== '0' ? data?.acao.slice(0, 64) : null,
          funcionalidade:
            data?.funcionalidade && data?.funcionalidade !== '0'
              ? data?.funcionalidade.slice(0, 64)
              : null,
          page: newPage,
          pageSize: rowsPerPage,
        })
        .then((response) => {
          if (response.data && response.data.content) {
            setTeenagerRecordAuditItem(response.data.content)
            handleTotalOfElements(response.data.totalElements)
            handleTotalOfPages(response.data.totalPages)
          }
        })
        .finally(() => handleBackdropClose())
    }
  }

  return {
    teenagerRecordAuditItem,
    handleBuscarAdolescentes,
    page,
    totalOfElements,
    handleNewPage,
    totalOfPages,
  }
}

export const useAudit = (id?: number) => {
  const {
    page,
    handleRowsPerPage,
    rows: { rowsOptions, rowsPerPage },
    elements: { totalOfElements, handleTotalOfElements },
    handleNewPage,
  } = usePagination()

  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const [auditItem, setAuditItem] = useState<AuditItem[]>([])

  const handleBuscarDados = async (
    data: TeenagerRecordAuditType,
    page: number,
    pageSize: number,
  ) => {
    if (data) {
      handleBackdropOpen()
      await client
        .get<BaseDataContent<AuditItem[]>>(`/ficha-adolescente/${id}/auditoria`, {
          ...data,
          acao: data?.acao !== '0' ? data?.acao : null,
          funcionalidade: data?.funcionalidade !== '0' ? data?.funcionalidade : null,
          page,
          pageSize,
        })
        .then((response) => {
          if (response.data && response.data.content) {
            setAuditItem(response.data.content)
            handleTotalOfElements(response.data.totalElements)
          }
        })
        .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
        .finally(() => handleBackdropClose())
    }
  }

  const handleDownloadAuditoria = async (data?: TeenagerRecordAuditType) => {
    if (data) {
      handleBackdropOpen()
      await client
        .getBlob<BaseData<DownloadResumePdf>>(`/ficha-adolescente/${id}/auditoria/download`, {
          ...data,
          acao: data?.acao !== '0' ? data?.acao : null,
          funcionalidade: data?.funcionalidade !== '0' ? data?.funcionalidade : null,
        })
        .then((response) => {
          if (response.data && response.data) {
            downloadFile(response, `relatorio_auditoria_ficha_adolescente_${id}.pdf`)
          }
        })
        .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
        .finally(() => handleBackdropClose())
    }
  }

  return {
    auditItem,
    handleBuscarDados,
    handleDownloadAuditoria,
    handleRowsPerPage,
    page,
    rowsPerPage,
    rowsOptions,
    totalOfElements,
    handleNewPage,
  }
}
